import { useCallback, useMemo } from 'react';
import { useAppSelector } from '@/store';
import { 
  isPaymentMethodExpired,
  isPaymentMethodExpiringSoon,
  validatePaymentAmount,
  calculatePaymentFees,
  IPaymentsFeeStructure
} from '@/utils/residents/paymentUtils';
import { classifyPaymentMethods } from '@/utils/residents/paymentMethods';

export interface PaymentMethodValidation {
  isValid: boolean;
  isExpired: boolean;
  isBlocked: boolean;
  errors: string[];
  warnings: string[];
}

export interface PaymentAmountValidation {
  isValid: boolean;
  error?: string;
  canPayBalanceUnderMin: boolean;
}

export interface PaymentFormValidation {
  isValid: boolean;
  errors: Record<string, string>;
  canProceed: boolean;
}

/**
 * Hook for comprehensive payment method validation
 * Matches web implementation validation logic
 */
export const usePaymentMethodValidation = (paymentMethod?: any): PaymentMethodValidation => {
  return useMemo(() => {
    if (!paymentMethod) {
      return {
        isValid: false,
        isExpired: false,
        isBlocked: false,
        errors: ['No payment method selected'],
        warnings: [],
      };
    }

    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if expired using consolidated utility
    const isExpired = isPaymentMethodExpired(paymentMethod);

    if (isExpired) {
      errors.push('Payment method has expired. Please update your payment information.');
    }

    // Check if blocked
    const isBlocked = Boolean(paymentMethod.blockedAt);
    if (isBlocked) {
      errors.push('The selected payment method is disabled because of previous declines. Please update the information before continuing.');
    }

    // Check for missing required fields
    if (!paymentMethod.last4 && !paymentMethod.name) {
      errors.push('Payment method is missing required information');
    }

    // Add warnings for expiring soon using consolidated utility
    if (!isExpired && isPaymentMethodExpiringSoon(paymentMethod, 30)) {
      warnings.push('Payment method expires soon. Consider updating your payment information.');
    }

    return {
      isValid: errors.length === 0,
      isExpired,
      isBlocked,
      errors,
      warnings,
    };
  }, [paymentMethod]);
};

/**
 * Hook for payment amount validation
 * Matches web implementation validation logic
 */
export const usePaymentAmountValidation = (
  amount: number,
  balanceDue: number,
  isCustomAmount: boolean,
  property?: any
): PaymentAmountValidation => {
  return useMemo(() => {
    const minPaymentAmount = property?.minPaymentAmount || 10;
    const canPayBalanceUnderMin = balanceDue > 0 && balanceDue < minPaymentAmount;
    
    const validation = validatePaymentAmount(
      amount,
      balanceDue,
      minPaymentAmount,
      isCustomAmount
    );

    return {
      isValid: validation.valid,
      error: validation.error,
      canPayBalanceUnderMin,
    };
  }, [amount, balanceDue, isCustomAmount, property]);
};

/**
 * Hook for comprehensive payment form validation
 * Combines all validation logic
 */
export const usePaymentFormValidation = (
  amount: number,
  balanceDue: number,
  isCustomAmount: boolean,
  paymentMethod?: any,
  property?: any
): PaymentFormValidation => {
  const methodValidation = usePaymentMethodValidation(paymentMethod);
  const amountValidation = usePaymentAmountValidation(amount, balanceDue, isCustomAmount, property);

  return useMemo(() => {
    const errors: Record<string, string> = {};

    // Payment method errors
    if (methodValidation.errors.length > 0) {
      errors.paymentMethod = methodValidation.errors[0];
    }

    // Amount validation errors
    if (!amountValidation.isValid && amountValidation.error) {
      errors.amount = amountValidation.error;
    }

    // Additional business logic validation
    if (amount <= 0) {
      errors.amount = 'Payment amount must be greater than zero';
    }

    const isValid = Object.keys(errors).length === 0;
    const canProceed = isValid && methodValidation.isValid && amountValidation.isValid;

    return {
      isValid,
      errors,
      canProceed,
    };
  }, [methodValidation, amountValidation, amount]);
};

/**
 * Hook for payment fee calculation with validation
 * Matches web implementation fee logic
 */
export const usePaymentFeeCalculation = (
  amount: number,
  paymentMethod?: any,
  feeStructure?: IPaymentsFeeStructure | null | undefined,
  property?: any
) => {
  return useMemo(() => {
    if (!paymentMethod || amount <= 0) {
      return {
        fee: 0,
        feeType: 'fixed' as const,
        total: amount,
        convenienceFee: 0,
        isValid: false,
      };
    }

    const calculation = calculatePaymentFees(amount, paymentMethod, feeStructure || null, property);
    
    return {
      ...calculation,
      isValid: true,
    };
  }, [amount, paymentMethod, feeStructure, property]);
};

/**
 * Hook for payment method selection and management
 */
export const usePaymentMethodSelection = (
  paymentMethods: any[] = [],
  selectedMethodId?: string | number
) => {
  return useMemo(() => classifyPaymentMethods(paymentMethods, selectedMethodId), [paymentMethods, selectedMethodId]);
};

/**
 * Hook for complete payment validation state
 * Provides all validation information needed for payment flow
 */
export const useCompletePaymentValidation = (
  amount: number,
  balanceDue: number,
  isCustomAmount: boolean,
  paymentMethod?: any,
  feeStructure?: IPaymentsFeeStructure | null,
  property?: any
) => {
  const formValidation = usePaymentFormValidation(amount, balanceDue, isCustomAmount, paymentMethod, property);
  const methodValidation = usePaymentMethodValidation(paymentMethod);
  const amountValidation = usePaymentAmountValidation(amount, balanceDue, isCustomAmount, property);
  const feeCalculation = usePaymentFeeCalculation(amount, paymentMethod, feeStructure, property);

  const canProceedToConfirmation = useCallback(() => {
    return (
      formValidation.canProceed &&
      methodValidation.isValid &&
      amountValidation.isValid &&
      feeCalculation.isValid &&
      amount > 0
    );
  }, [formValidation.canProceed, methodValidation.isValid, amountValidation.isValid, feeCalculation.isValid, amount]);

  return {
    form: formValidation,
    method: methodValidation,
    amount: amountValidation,
    fees: feeCalculation,
    canProceedToConfirmation: canProceedToConfirmation(),
    hasAnyErrors: !formValidation.isValid || !methodValidation.isValid || !amountValidation.isValid,
  };
};
